import { addMessage, setMessages, removeMessageById, updateMessage } from '../slices/messages-slice';
import {
    setUi,
    resetStreamingUi,
    startStreaming,
    finishStreaming,
    closeConversationsList,
    openConversationsList,
    startLoadingConversations,
    finishLoadingConversations,
    openFeedback,
    closeFeedback,
} from '../slices/ui-slice';
import {
    setConversationId,
    setLastMessageId,
    resetConversation,
    removeConversation,
    setConversations,
    updateConversationTitle
} from '../slices/conversations-slice';
import { setUsageLimitHit } from '../slices/context-slice';
import { consumeBrandContextReset } from '../slices/brand-discovery-slice';
import * as api from '../services/chat-api';
import * as stream from '../services/chat-stream-service';
import bridge from 'default/side-chat-bridge';

const lastSystemId = (msgs) => [...msgs].reverse().find(m => m.turn === 'system')?.id ?? null;

function buildExtraParams(getState, overrides = {}) {
    const uiState = window.getLoomlyAiUiState ? window.getLoomlyAiUiState() : {};
    const params = {
        sidebar_expanded: uiState.sidebar_expanded || false,
        chat_open: uiState.open || false,
        is_side_chat: uiState.is_side_chat || false,
        ...overrides
    };
    if (getState().context.isSideChat) {
        const postDraft = bridge.get('postDraft');
        if (postDraft) params.post_draft_context = JSON.stringify(postDraft);
        const analyticsCtx = bridge.get('analyticsContext');
        if (analyticsCtx) params.analytics_context = JSON.stringify(analyticsCtx);
    }
    return params;
}

function updateChatUrl(getState, calendarId, conversationId, { replace = false } = {}) {
    if (getState().context.isSideChat) {
        const url = new URL(window.location);
        if (url.searchParams.has('chat_id')) {
            url.searchParams.set('chat_id', conversationId || 'new');
            window.history.replaceState(null, '', url);
        }
    } else {
        const path = conversationId
            ? `/calendars/${calendarId}/chat/${conversationId}`
            : `/calendars/${calendarId}/chat`;
        window.history[replace ? 'replaceState' : 'pushState'](null, '', path);
    }
}

export const resetChatState = () => (dispatch) => {
    stream.abortActive();
    dispatch(setMessages([]));
    dispatch(resetConversation());
    dispatch(resetStreamingUi());
};

export const initChat = ({ initialConversationId, initialMessages, scrollToBottom }) => (dispatch) => {
    if (initialConversationId) {
        dispatch(setConversationId(initialConversationId));
    } else {
        dispatch(resetConversation());
    }

    const all = initialMessages || [];
    const hasProcessing = all.some(m => m.status === 'processing');
    const complete = all.filter(m => m.status !== 'processing');

    dispatch(setMessages(complete));
    dispatch(setUi({ isLoading: hasProcessing, streamingText: '', status: null }));

    if (complete.length > 0) {
        dispatch(setLastMessageId(lastSystemId(complete)));
        queueMicrotask(scrollToBottom);
    }
};

export const openConversation = ({ calendarId, id, fromConversationId, scrollToBottom }) => async (dispatch, getState) => {
    stream.abortActive();
    dispatch(closeConversationsList());
    dispatch(resetStreamingUi());

    const response = await api.getConversationMessages(calendarId, id, { fromConversationId });
    const all = response.messages || [];
    const hasProcessing = all.some(m => m.status === 'processing');
    const complete = all.filter(m => m.status !== 'processing');

    dispatch(setMessages(complete));
    dispatch(setUi({ isLoading: hasProcessing, streamingText: '', status: null }));
    dispatch(setLastMessageId(lastSystemId(complete)));
    dispatch(setConversationId(id));

    updateChatUrl(getState, calendarId, id);
    queueMicrotask(scrollToBottom);
};

export const newChat = ({ calendarId }) => (dispatch, getState) => {
    dispatch(resetChatState());
    updateChatUrl(getState, calendarId, null);
};

export const stopStreaming = ({ calendarId }) => (dispatch, getState) => {
    const { conversationId } = getState().conversations;
    const lastUserMessage = [...getState().messages.history].reverse().find(m => m.turn === 'user');

    stream.abortActive();
    if (conversationId) stream.cancelStream(calendarId, conversationId);
    if (lastUserMessage) dispatch(removeMessageById(lastUserMessage.id));
    dispatch(resetStreamingUi());
};

const handleRequestError = (dispatch, getState) => (jqXHR) => {
    if (jqXHR.statusText === 'abort' || jqXHR.status !== 402) return;

    const lastUser = [...getState().messages.history].reverse().find(m => m.turn === 'user');
    if (lastUser) dispatch(removeMessageById(lastUser.id));
    dispatch(finishStreaming());
    dispatch(setUsageLimitHit());
    bridge.emit('usageLimitHit', true);
};

export const sendMessage = ({ calendarId, text, scrollToBottom, attachments = [], consumeResetContext = false }) => (dispatch, getState) => {
    const { conversationId, lastMessageId } = getState().conversations;
    let resetContext = false;

    if (consumeResetContext && getState().brandDiscovery.resetContextPending) {
        resetContext = true;
        dispatch(consumeBrandContextReset());
    }

    dispatch(addMessage({
        content: text,
        turn: 'user',
        attachments: attachments.map(a => ({ url: a.url, type: a.isVideo ? 'video' : 'image' })),
        rawAttachments: attachments
    }));
    dispatch(startStreaming());
    scrollToBottom();

    const extraParams = buildExtraParams(getState);
    if (resetContext) extraParams.reset_context = true;
    const messageData = { text, prevMessageId: lastMessageId, attachments, extraParams };
    const onFail = handleRequestError(dispatch, getState);

    if (conversationId) {
        stream.sendToExistingConversation(calendarId, { ...messageData, conversationId }).fail(onFail);
    } else {
        stream.startNewMessageStream(calendarId, messageData, (userMessage) => {
            const convId = userMessage.conversation_id;
            if (!convId) return;
            dispatch(setConversationId(convId));
            updateChatUrl(getState, calendarId, convId, { replace: true });
        }).fail(onFail);
    }
};

export const regenerateLast = ({ calendarId, scrollToBottom }) => (dispatch, getState) => {
    const messages = getState().messages.history;
    const { conversationId } = getState().conversations;

    const lastUserMessage = [...messages].reverse().find(m => m.turn === 'user');
    const lastGoodSystemMessage = [...messages].reverse().find(
        m => m.turn === 'system' && m.type !== 'error' && m.status !== 'failed'
    );

    if (!lastUserMessage?.content || !lastGoodSystemMessage) return;

    dispatch(removeMessageById(lastGoodSystemMessage.id));
    dispatch(startStreaming({ showActions: false }));
    scrollToBottom();

    const extraParams = buildExtraParams(getState, { tracking_action: 'regenerate' });
    stream.sendToExistingConversation(calendarId, {
        text: lastUserMessage.content,
        conversationId,
        prevMessageId: lastUserMessage.id,
        replacesMessageId: lastGoodSystemMessage.id,
        extraParams
    });
};

export const retryLastError = ({ calendarId, lastInputText, scrollToBottom }) => (dispatch, getState) => {
    const messages = getState().messages.history;
    const { conversationId, lastMessageId } = getState().conversations;

    const lastUserMessage = [...messages].reverse().find(m => m.turn === 'user');
    const lastMsg = messages[messages.length - 1];
    const errorMessage = (lastMsg?.turn === 'system' && (lastMsg.status === 'failed' || lastMsg.type === 'error'))
        ? lastMsg
        : null;

    const text = lastInputText || lastUserMessage?.content || '';
    if (!errorMessage) return;

    dispatch(removeMessageById(errorMessage.id));
    dispatch(startStreaming());
    scrollToBottom();

    const extraParams = buildExtraParams(getState, { tracking_action: 'retry' });

    const onFail = handleRequestError(dispatch, getState);

    if (conversationId) {
        stream.sendToExistingConversation(calendarId, {
            text,
            conversationId,
            prevMessageId: lastMessageId,
            replacesMessageId: errorMessage.id,
            extraParams
        }).fail(onFail);
    } else {
        const rawAttachments = lastUserMessage?.rawAttachments || [];
        const displayAttachments = lastUserMessage?.attachments || [];
        if (lastUserMessage) dispatch(removeMessageById(lastUserMessage.id));
        dispatch(addMessage({ content: text, turn: 'user', attachments: displayAttachments, rawAttachments }));
        stream.startNewMessageStream(calendarId, { text, prevMessageId: null, attachments: rawAttachments, extraParams }, (userMessage) => {
            const convId = userMessage.conversation_id;
            if (!convId) return;
            dispatch(setConversationId(convId));
            updateChatUrl(getState, calendarId, convId, { replace: true });
        }).fail(onFail);
    }
};

export const fetchConversations = ({ calendarId, currentConversationId }) => async (dispatch) => {
    dispatch(openConversationsList());
    dispatch(startLoadingConversations());
    try {
        const response = await api.getConversations(calendarId, { currentConversationId });
        dispatch(setConversations(response.conversations || []));
    } finally {
        dispatch(finishLoadingConversations());
    }
};

export const renameConversation = ({ calendarId, id, title }) => async (dispatch) => {
    await api.renameConversation(calendarId, id, title);
    dispatch(updateConversationTitle({ id, title }));
};

export const deleteConversation = ({ calendarId, id }) => async (dispatch, getState) => {
    const { conversationId } = getState().conversations;

    await api.deleteConversation(calendarId, id);
    dispatch(removeConversation(id));

    if (id === conversationId) {
        dispatch(setMessages([]));
        dispatch(resetConversation());
        updateChatUrl(getState, calendarId, null);
    }
};

export const upvoteMessage = ({ messageId, value }) => (dispatch, getState) => {
    const { conversationId } = getState().conversations;
    const calendarId = getState().context.calendar?.id;
    const message = getState().messages.history.find(m => m.id === messageId);
    if (!calendarId || !conversationId || !message) return;

    const upvoted = message.upvoted === value ? null : value;
    dispatch(updateMessage({ id: messageId, upvoted }));
    api.upvoteMessage(calendarId, conversationId, messageId, upvoted);

    if (value === false && message.upvoted !== false) {
        dispatch(openFeedback(messageId));
    } else {
        dispatch(closeFeedback());
    }
};

export const submitMessageReview = () => (dispatch, getState) => {
    const { feedbackMessageId, feedbackText } = getState().ui;
    const { conversationId } = getState().conversations;
    const calendarId = getState().context.calendar?.id;
    if (!calendarId || !conversationId || !feedbackMessageId) return;

    api.submitReview(calendarId, conversationId, feedbackMessageId, feedbackText);
    dispatch(closeFeedback());
};
